import { Component, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * A content card component that displays an icon, title, and optional subtitle.
 * This is a display-only component with no interaction/click handling.
 * Based on Figma designs with rounded corners and customizable colors.
 *
 * Figma URLs:
 * - With subtitle: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1058-36057
 * - Without subtitle: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1058-36119
 * - Card container: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1058-36054
 *
 * @example
 * ```html
 * <!-- With subtitle -->
 * <app-content-card
 *   title="Global Impact"
 *   subtitle="Make a difference worldwide">
 * </app-content-card>
 *
 * <!-- Without subtitle -->
 * <app-content-card
 *   title="Global Impact">
 * </app-content-card>
 *
 * <!-- Custom colors and icon -->
 * <app-content-card
 *   [icon]="customSvg"
 *   title="Custom Card"
 *   subtitle="With custom styling"
 *   borderColor="#e5e7eb"
 *   backgroundColor="#ffffff">
 * </app-content-card>
 * ```
 */
@Component({
  selector: 'app-content-card',
  templateUrl: './content-card.component.html',
  styleUrls: ['./content-card.component.scss'],
  standalone: false,
})
export class ContentCardComponent {
  /**
   * Icon to display - can be either:
   * - Inline SVG string (e.g., "<svg>...</svg>")
   * - Path to SVG file (e.g., "assets/icons/my-icon.svg")
   * Defaults to a globe icon if not provided.
   */
  icon = input<string>(`<svg width="25" height="26" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12.4999 23.8337C18.2529 23.8337 22.9166 18.9834 22.9166 13.0003C22.9166 7.01724 18.2529 2.16699 12.4999 2.16699C6.74695 2.16699 2.08325 7.01724 2.08325 13.0003C2.08325 18.9834 6.74695 23.8337 12.4999 23.8337Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12.4999 2.16699C9.82516 5.08783 8.33325 8.96679 8.33325 13.0003C8.33325 17.0339 9.82516 20.9128 12.4999 23.8337C15.1747 20.9128 16.6666 17.0339 16.6666 13.0003C16.6666 8.96679 15.1747 5.08783 12.4999 2.16699Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M2.08325 13H22.9166" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`);

  /**
   * Title text displayed prominently
   */
  title = input.required<string>();

  /**
   * Optional subtitle text displayed below the title.
   * When not provided, the card layout adjusts to center the title.
   */
  subtitle = input<string | undefined>(undefined);

  /**
   * Border color of the card.
   * Defaults to design token value if not provided.
   */
  borderColor = input<string | undefined>(undefined);

  /**
   * Background color of the card.
   * Defaults to design token value if not provided.
   */
  backgroundColor = input<string | undefined>(undefined);

  /**
   * Color of the icon (applies to both inline SVG and SVG files).
   * Defaults to white.
   */
  iconColor = input<string>('white');

  /**
   * Background color of the icon container.
   * Defaults to design token value if not provided.
   */
  iconBackgroundColor = input<string | undefined>(undefined);

  /**
   * Whether to use the projected icon component via ng-content instead of the icon string.
   * When true, renders ng-content with [iconSlot] selector.
   * When false (default), uses the icon string input.
   */
  useIconSlot = input<boolean>(false);

  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Determines if the icon is a file path (SVG file) or inline SVG string
   */
  isIconFile(): boolean {
    const iconValue = this.icon();
    return iconValue?.endsWith('.svg') || iconValue?.includes('/') && !iconValue?.trim().startsWith('<');
  }

  /**
   * Returns sanitized HTML for inline SVG icons.
   * This bypasses Angular's default sanitization to allow SVG rendering.
   */
  getSafeIcon(): SafeHtml {
    const iconValue = this.icon();
    // Return empty string if icon is undefined/null to prevent displaying "undefined"
    return this.sanitizer.bypassSecurityTrustHtml(iconValue || '');
  }
}
